import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React from 'react';

import { findingPhase } from '@/pages/externals/pos-menu-masters/service';
import {
  MosPosFinding,
  MosPosValidateSummary,
} from '@/services/MosPosMenuMaster';

type Props = {
  summary: MosPosValidateSummary;
  findings: MosPosFinding[];
  truncated: boolean;
};

/** 段階ごとの検知件数。findings が truncate されている場合は表示分の集計になります。 */
type PhaseCount = { errors: number; warnings: number; infos: number };

const countPhase = (
  findings: MosPosFinding[],
  phase: 'format' | 'reference'
): PhaseCount =>
  findings
    .filter((f) => findingPhase(f.rule) === phase)
    .reduce<PhaseCount>(
      (acc, f) => {
        if (f.severity === 'error') acc.errors += 1;
        else if (f.severity === 'warning') acc.warnings += 1;
        else acc.infos += 1;
        return acc;
      },
      { errors: 0, warnings: 0, infos: 0 }
    );

/** Warning / Info だけの場合に添える補足。無ければ空文字。 */
const nonBlockingNote = (c: PhaseCount): string => {
  const parts: string[] = [];
  if (c.warnings > 0) parts.push(`Warning ${c.warnings}件`);
  if (c.infos > 0) parts.push(`Info ${c.infos}件`);
  if (parts.length === 0) return '';
  return `（${parts.join('・')}。登録はブロックしません）`;
};

/**
 * ルールごとの日本語文言です。API の message は英語のログ用なので画面には出さず、
 * rule と severity から画面側で文言を決めます。
 */
const RULE_MESSAGES: Record<string, string> = {
  'V-001': 'ファイルがUTF-8ではないか、BOMが付いています。',
  'V-002': 'ヘッダーの項目名または並び順が正しくありません。',
  'V-003': '商品コードは6桁の数字で入力してください。',
  'V-004': '必須項目が入力されていません。',
  'V-005': 'チョイスコードは4桁の数字で入力してください。',
  'V-006': '日付は YYYY-MM-DD 形式の実在する日付で入力してください。',
  'V-007': '販売開始日が販売終了日より後になっています。',
  'V-008': 'フラグは 0 か 1 で入力してください。',
  'V-009': '価格は0以上の整数で入力してください。',
  'V-010': '価格差額は整数で入力してください。',
  'V-011': '数量は1以上、表示順は0以上で入力してください。',
  'V-011a': '特殊注文コードは半角数字5桁で入力してください。',
  'V-011b':
    '特殊注文コードに値がある場合は、Webメニューコードを空にできません。',
  'V-012': '種別ごとの必須項目または不要項目が正しくありません。',
  'V-013': '重複、項目の食い違い、または販売期間の重なり・抜けがあります。',
  'V-014':
    'PLASTIC グループとして確認済みのコードではありません。登録は PLASTIC とみなして続行しますので、コードのグループを確認してください。',
  'R-001': '親商品がマスタに存在しません。',
  'R-002': '子商品がマスタに存在しません。',
  'R-003': 'セット親商品がマスタに存在しません。',
  'R-004': '同じ内容のリレーションが重複しています。',
  'R-005':
    'チョイスヘッダーに対応するセット行が見つかりません（登録はブロックしません）。',
  'R-006': 'チョイスに対応する選択肢がありません。',
  'R-007':
    'チョイスの見出しが見つからないため、チョイスコードを表示名に使用します。',
  'R-008': '営業日時点で有効な販売期間が正しく設定されていません。',
  'R-009': '区分3の商品がセット親として使われています。',
  'R-010': 'どのリレーションからも参照されていない商品です。',
  'LIMIT-ROWS': '1ファイルあたりの行数上限を超えています。',
  'LIMIT-BYTES': '1ファイルあたりのサイズ上限を超えています。',
};

const findingMessage = (finding: MosPosFinding): string =>
  RULE_MESSAGES[finding.rule] ?? '内容を確認してください。';

const fileLabel = (file: MosPosFinding['file']): string =>
  file === 'master' ? '親商品マスタ' : '子商品リレーション';

const validationAlertStyle = (
  status: 'success' | 'error' | 'info'
): React.CSSProperties => {
  const palette =
    status === 'success'
      ? { background: '#F0FFF4', border: '#9AE6B4', color: '#276749' }
      : status === 'error'
      ? { background: '#FFF5F5', border: '#FEB2B2', color: '#C53030' }
      : { background: '#EEF3FA', border: '#7FB3F0', color: '#1D4ED8' };
  return {
    alignItems: 'flex-start',
    background: palette.background,
    border: `1px solid ${palette.border}`,
    borderRadius: '5px',
    color: palette.color,
    display: 'flex',
    fontSize: '13px',
    gap: '10px',
    lineHeight: 1.55,
    padding: '11px 14px',
  };
};

const validationIconStyle: React.CSSProperties = {
  flexShrink: 0,
  height: '17px',
  marginRight: 0,
  marginTop: '1px',
  width: '17px',
};

const errorTableHeaderStyle: React.CSSProperties = {
  background: '#FFF5F5',
  borderBottom: '1px solid #FEB2B2',
  color: '#C53030',
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.03em',
  padding: '7px 10px',
  position: 'sticky',
  textTransform: 'uppercase',
  top: 0,
  zIndex: 1,
};

const errorTableCellStyle: React.CSSProperties = {
  borderBottom: '1px solid #E2E8F0',
  padding: '7px 10px',
  verticalAlign: 'top',
};

const severityStyle = (
  severity: MosPosFinding['severity']
): React.CSSProperties => {
  switch (severity) {
    case 'error':
      return {
        background: '#FFF5F5',
        border: '1px solid #FEB2B2',
        color: '#C53030',
      };
    case 'warning':
      return {
        background: '#FFFAF0',
        border: '1px solid #FBD38D',
        color: '#975A16',
      };
    default:
      return {
        background: '#EEF3FA',
        border: '1px solid #7FB3F0',
        color: '#1D4ED8',
      };
  }
};

const severityLabel = (severity: MosPosFinding['severity']): string =>
  severity === 'error' ? 'Error' : severity === 'warning' ? 'Warning' : 'Info';

/**
 * バリデーション結果。設計書 No.5（フォーマット）と No.6（参照整合性）を分けて
 * 表示し、Error が0件なら No.8 の成功メッセージを出します。
 */
export const ValidationResult: React.FC<Props> = ({
  summary,
  findings,
  truncated,
}) => {
  const format = countPhase(findings, 'format');
  const reference = countPhase(findings, 'reference');

  return (
    <Box>
      {/* No.5 フォーマットバリデーション */}
      <Alert
        status={format.errors > 0 ? 'error' : 'success'}
        style={validationAlertStyle(format.errors > 0 ? 'error' : 'success')}
        mb={3}
        data-testid="validation-format"
      >
        <AlertIcon style={validationIconStyle} />
        <Box>
          <Text as="span" fontWeight="bold">
            {format.errors > 0
              ? 'フォーマットバリデーション'
              : 'フォーマットバリデーション成功'}
          </Text>
          {' — 親商品マスタ '}
          {summary.master_rows}行
          {summary.documents !== undefined
            ? `（${summary.documents}メニュー）`
            : ''}
          {'／リレーション '}
          {summary.relation_rows}行 を読み込みました。
          {format.errors > 0
            ? `V-003〜V-016 のうち ${format.errors}件 がErrorで不合格です。`
            : `V-003〜V-016 すべて合格しました。${nonBlockingNote(format)}`}
        </Box>
      </Alert>

      {/* No.6 参照整合性バリデーション */}
      <Alert
        status={reference.errors > 0 ? 'error' : 'success'}
        style={validationAlertStyle(reference.errors > 0 ? 'error' : 'success')}
        mb={3}
        data-testid="validation-reference"
      >
        <AlertIcon style={validationIconStyle} />
        <Box>
          <Text as="span" fontWeight="bold">
            {reference.errors > 0 ? '参照整合性バリデーション' : '参照整合性OK'}
          </Text>
          {' — '}
          {reference.errors > 0
            ? `R-001〜R-011 のうち ${reference.errors}件 がError${
                reference.warnings > 0
                  ? `、${reference.warnings}件 がWarning（登録はブロックしません）`
                  : ''
              }です。`
            : `R-001〜R-011 すべて合格しました。${nonBlockingNote(reference)}`}
        </Box>
      </Alert>

      {/* No.8 Error 0件のときだけ */}
      {summary.errors === 0 && (
        <Alert
          status="success"
          style={validationAlertStyle('success')}
          mb={3}
          data-testid="validation-ok"
        >
          <AlertIcon style={validationIconStyle} />
          Errorレベルの検知は0件です。「取込内容の確認」に進めます。
        </Alert>
      )}

      {/* No.9 Error があるとき */}
      {summary.errors > 0 && (
        <Alert
          status="error"
          style={validationAlertStyle('error')}
          mb={3}
          data-testid="validation-blocked"
        >
          <AlertIcon style={validationIconStyle} />
          {summary.errors}
          件のエラーにより登録できません。エラー内容を確認し、エラーを解消したCSVを再アップロードしてください。
        </Alert>
      )}

      {truncated && (
        <Alert
          status="info"
          style={validationAlertStyle('info')}
          mb={3}
          data-testid="findings-truncated"
        >
          <AlertIcon style={validationIconStyle} />
          表示は1,000件までです。上の件数は全件の実数を表しています。
        </Alert>
      )}

      {findings.length > 0 && (
        <>
          <Text fontSize="13px" color="#2D3748" fontWeight="bold" mb={1}>
            エラー行一覧（対象ファイル・行番号・コード・特殊注文コード・カラム・ルールID・Severity・内容）
          </Text>
          <Box
            data-testid="findings-table-wrap"
            style={{
              border: '1px solid #FEB2B2',
              borderRadius: '5px',
              maxHeight: '640px',
              marginTop: '4px',
              overflowX: 'auto',
              overflowY: 'auto',
            }}
          >
            <Table
              size="sm"
              style={{
                borderCollapse: 'collapse',
                fontSize: '12px',
                minWidth: '916px',
                width: '100%',
              }}
            >
              <Thead
                style={{
                  background: '#FFF5F5',
                  borderBottom: '1px solid #FEB2B2',
                }}
              >
                <Tr>
                  <Th style={{ ...errorTableHeaderStyle, width: '140px' }}>
                    対象ファイル
                  </Th>
                  <Th
                    isNumeric
                    style={{ ...errorTableHeaderStyle, width: '44px' }}
                  >
                    行
                  </Th>
                  <Th style={{ ...errorTableHeaderStyle, width: '76px' }}>
                    コード
                  </Th>
                  <Th style={{ ...errorTableHeaderStyle, width: '96px' }}>
                    特殊注文コード
                  </Th>
                  <Th style={{ ...errorTableHeaderStyle, width: '210px' }}>
                    カラム
                  </Th>
                  <Th style={{ ...errorTableHeaderStyle, width: '66px' }}>
                    ルールID
                  </Th>
                  <Th style={{ ...errorTableHeaderStyle, width: '66px' }}>
                    Severity
                  </Th>
                  <Th style={errorTableHeaderStyle}>内容</Th>
                </Tr>
              </Thead>
              <Tbody>
                {findings.map((finding) => (
                  <Tr
                    /* 同じ file/row/カラムに別コードの V-014 が並ぶため key に含めます。 */
                    key={`${finding.file}-${finding.row}-${finding.rule}-${
                      finding.column ?? ''
                    }-${finding.special_order_code ?? ''}`}
                    data-testid="finding-row"
                  >
                    <Td style={errorTableCellStyle}>
                      {fileLabel(finding.file)}
                    </Td>
                    <Td isNumeric style={errorTableCellStyle}>
                      {finding.row}
                    </Td>
                    <Td
                      style={{
                        ...errorTableCellStyle,
                        fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                      }}
                    >
                      {finding.menu_code ?? '—'}
                    </Td>
                    <Td
                      data-testid="finding-special-order-code"
                      style={{
                        ...errorTableCellStyle,
                        fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                      }}
                    >
                      {finding.special_order_code ?? '—'}
                    </Td>
                    <Td style={errorTableCellStyle}>{finding.column ?? '—'}</Td>
                    <Td style={errorTableCellStyle}>
                      <Text
                        as="span"
                        style={{
                          background: '#FFF5F5',
                          border: '1px solid #FEB2B2',
                          borderRadius: '4px',
                          color: '#C53030',
                          fontFamily:
                            'ui-monospace, Menlo, Consolas, monospace',
                          fontSize: '11px',
                          fontWeight: 700,
                          padding: '2px 8px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {finding.rule}
                      </Text>
                    </Td>
                    <Td style={errorTableCellStyle}>
                      <Badge
                        data-testid="finding-severity"
                        style={{
                          ...severityStyle(finding.severity),
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 700,
                          padding: '2px 8px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {severityLabel(finding.severity)}
                      </Badge>
                    </Td>
                    <Td style={errorTableCellStyle}>
                      {findingMessage(finding)}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
};
